import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import {
  CHART_SWITCH_DISPLAY_ORDER,
  CHART_TYPE_ICON,
} from '@dbl/core/chartEditor';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { DATALAB_ANALYSIS_ERROR_CODE } from '@dbl/core/global';
import { notification } from 'tea-component';
import { useChartSettingsSelector } from '../ChartSettingsV2/context';
import { mergeCustomizeSettingsForApi } from '../ChartSettingsV2/services/api-upload';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import type { IColumnInfo } from '@dbl/core/dataSource';
export interface IChartTypeSwitchBarProps {
  table: any;
  mode: TChartCalculateMode;
  chartData: any;
  /** 与 IChartConfig.chartSwitch 一致 */
  chartSwitch?: { show: boolean; chartTypes: string[]; };
  // 顶部当前选中图表
  curChartType: TChartType;
  // 当前切换条图表类型
  chartType: TChartType;
  // 图表切换条的渲染类型
  chartSwitchRenderType: TChartType | null;
  namespace: string;
  dispatch: (action: any) => void;
  isEn: boolean;
  gameCode: string;
  columnDimensions?: IColumnInfo[];
  columnMetrics?: IColumnInfo[];
  mode?: TChartCalculateMode;
  filterSignature?: string;
  cacheSignatureMap?: Record<string, string>;
  onCacheSignatureUpdate?: (type: TChartType, signature: string) => void;
  onSelectChartType?: (type: TChartType) => void;
}

/**
 * 图表类型切换条：当 chart_switch 开启且在 Table/Line/Bar 时，在图表上方展示可切换的图表类型按钮，点击后请求接口并切换
 */
const ChartTypeSwitchBar: React.FC<IChartTypeSwitchBarProps> = ({
  table,
  mode,
  chartData,
  chartSwitch,
  curChartType,
  chartType,
  chartSwitchRenderType,
  namespace: modelNamespace,
  dispatch,
  isEn,
  gameCode,
  columnDimensions = [],
  columnMetrics = [],
  mode = ChartCalculateMode.Aggregate,
  filterSignature = '',
  cacheSignatureMap = {},
  onCacheSignatureUpdate,
  onSelectChartType,
}) => {
  const { formatMessage } = useIntl();
  const baseFromContext = useChartSettingsSelector((s) => s.base);

  const show =
    chartSwitch?.show &&
    Array.isArray(chartSwitch?.chartTypes) &&
    chartSwitch?.chartTypes?.length > 1 &&
    [ChartType.Table, ChartType.Line, ChartType.Bar].includes(chartType as TChartType) &&
    (chartType === ChartType.Table ? mode === ChartCalculateMode.Aggregate : true);

  const handleSwitch = useCallback(
    (type: string): void => {
      const hasTableData = type === ChartType.Table && table?.data?.length > 0;
      const hasLineData = type === ChartType.Line && chartData?.lineChart?.seriesData?.length > 0;
      const hasBarData = type === ChartType.Bar && chartData?.barChart?.seriesData?.length > 0;
      const hasChartData = hasTableData || hasLineData || hasBarData;
      // 筛选条件未变化才可复用缓存；变化后强制请求，避免 VS 对比时间变更仍命中旧数据。
      if (hasChartData && cacheSignatureMap[type] === filterSignature) {
        return;
      }
      const switchTypes = (chartSwitch?.chartTypes || []) as TChartType[];
      const customizeSetting = mergeCustomizeSettingsForApi(switchTypes, {
        base: baseFromContext as Record<string, unknown>,
        draft: {},
        columnDimensions,
        columnMetrics,
        mode,
      });

      dispatch({
        type: `${modelNamespace}/getDataAnlysis`,
        payload: {
          isEn,
          customizeSetting,
          game_code: gameCode,
          refreshType: type,
          // 图表类型切换不影响 SQL，复用同一个 cache_key。
          reuseCacheKey: true,
          isGetTableSheet: type === ChartType.Table,
        },
        callback: (payload: any) => {
          if (payload?.errorMsg && payload?.errorCode !== 40005) {
            notification.error({
              className: 'dbl-analysis-msg',
              description: DATALAB_ANALYSIS_ERROR_CODE?.includes(payload?.errorCode)
                ? formatMessage(
                  { id: `dbl_error_${payload?.errorCode || '1'}` },
                  { name: payload?.errorData, msg: payload?.errorMsg }
                )
                : payload?.errorMsg,
              duration: 3000,
            });
          }
          dispatch({
            type: `${modelNamespace}/save`,
            payload: {
              ...(payload || {}),
              analysisRunning: false,
              analysisRefreshRunning: false,
              analysisReqId: '',
              dragState: { currentDrag: '', errorDrag: {} },
            },
          });
          onCacheSignatureUpdate?.(type as TChartType, filterSignature);
        },
      });
    },
    [
      table,
      chartData,
      chartSwitch?.chartTypes,
      columnDimensions,
      columnMetrics,
      mode,
      chartType,
      curChartType,
      dispatch,
      formatMessage,
      gameCode,
      isEn,
      modelNamespace,
      filterSignature,
      cacheSignatureMap,
      onCacheSignatureUpdate,
      baseFromContext,
    ]
  );

  /** 避免 show=false 或依赖抖动时反复触发 handleSwitch 造成死循环 */
  const autoCorrectKeyRef = useRef('');

  // 切换图表类型，如果类型不一致需要重新请求数据
  useEffect(() => {
    if (show === undefined || chartSwitchRenderType === null) {
      autoCorrectKeyRef.current = '';
      return;
    }
    const types = chartSwitch?.chartTypes || [];
    const dedupeKey = `${types?.join('|')}::${chartType}::${show}`;
    if (autoCorrectKeyRef.current === dedupeKey) {
      return;
    }
    // 如果图表类型不匹配，则切换到第一个图表类型
    if (!types?.includes(chartType) || chartSwitchRenderType !== chartType) {
      autoCorrectKeyRef.current = dedupeKey;
      onSelectChartType?.(types?.[0] as TChartType);
      handleSwitch(types?.[0] as TChartType);
    }
  }, [chartType, chartSwitch?.chartTypes, handleSwitch, onSelectChartType, show]);

  if (!show) return null;

  const orderedTypes = [...(chartSwitch?.chartTypes || [])]?.sort(
    (a, b) => CHART_SWITCH_DISPLAY_ORDER?.indexOf(a as TChartType) - CHART_SWITCH_DISPLAY_ORDER?.indexOf(b as TChartType)
  );

  return (
    <div className="dbl-chart-type-switch-bar">
      {orderedTypes.map((type) => {
        const isActive = type === chartType;
        return <div
          key={type}
          className={classNames('table-analysis-iconbox', {
            'table-analysis-iconbox-active': isActive,
          })}
          onClick={() => {
            if (!isActive) {
              onSelectChartType?.(type as TChartType);
              handleSwitch(type);
            }
          }}
        >
          <SvgIcon
            className="table-analysis-icon"
            type={CHART_TYPE_ICON[type as TChartType] ?? ''}
          />
        </div>;
      })}
    </div>
  );
};

export default ChartTypeSwitchBar;

